// PROVENANCE: C:\Valentin\shared\guide\flow-layout.ts @ d32c53d (main, H3a), copied 2026-10-11 (H3b-6, Ask Olivia)
// CHANGES: these three lines; nothing else (Valentin's own header below names ARIS as its origin). web/src/lib/guide/flow-layout.ts
//          is the same text, held to it by server/tests/unit/h3b-diagrams.test.ts.
// PROVENANCE: C:\ARIS\frontend\src\lib\workshop\diagram.ts @ 5d37545 (branch post-seb-governance-pi), adapted 2026-10-11
// CHANGES: H3a's diagrams of "How it works": the sheet carries no print fields (group, short, talk, stepNotes, tables); the
//          kinds are Valentin's eight (step, decision, person, ai, rule, data, external, output; ai is dashed: proposed, not
//          decided); a box's step badge is its place in the walk; a box names routes and rule cards instead of one route and
//          tables; every text is 14 px or more (Valentin's minimum), so the grid is narrower and the kind tag inside a box is
//          gone (the key names the shapes); a row lane's label is written across a left column instead of rotated; the
//          overflow check also refuses a word wider than its box and a lane label wider than its lane; the string renderer
//          and the print CSS are not taken; the CSS names its tokens --fd-* with a light and a dark set.

/**
 * flow-layout.ts: a diagram of "How it works", as data turned into drawing primitives (H3a). Pure: no React, no DOM, no
 * fetch, so the browser, Node and a test lay a sheet out identically, and `overflowingNodes()` lets a test refuse a sheet
 * whose words do not fit their boxes. Self-contained (no import), so the family's other products can copy it with
 * src/components/help/FlowDiagram.tsx.
 *
 * Colour is not carried here: every primitive names a CSS class, and FLOW_CSS maps the classes to ink, rule and card tokens.
 * The shape carries the meaning, so a drawing reads in black and white and in either theme.
 */

/** What a box is, which decides its shape. */
export type FlowKind =
  | 'step' // one act in a way of working
  | 'decision' // a person decides (four eyes, an approval, a sign-off)
  | 'person' // a person or a role
  | 'ai' // what an AI model proposes: dashed, because it is proposed, not decided
  | 'rule' // code or a rule decides (a rule card)
  | 'data' // what is held
  | 'external' // outside the product: a model provider, the client
  | 'output'; // what leaves the product: a report, a pack

export const FLOW_KINDS: readonly FlowKind[] = ['step', 'decision', 'person', 'ai', 'rule', 'data', 'external', 'output'];

export interface FlowNode {
  id: string;
  /** Grid column and row of the top-left cell. */
  col: number;
  row: number;
  /** Width and height in cells (default 1). */
  w?: number;
  h?: number;
  kind: FlowKind;
  label: string;
  /** A short line under the label, drawn in the box. */
  sub?: string;
  /** The longer explanation, shown when the box is opened. */
  detail?: string;
  /** Where it lives in the product: route patterns (":engagementId" and the like), resolved by the host. */
  routes?: string[];
  /** The rule cards that apply, by id. */
  rules?: string[];
}

export type FlowEdgeStyle = 'normal' | 'strong' | 'soft';
export type FlowSide = 'left' | 'right' | 'top' | 'bottom';

export interface FlowEdge {
  from: string;
  to: string;
  label?: string;
  /** `strong` is the main path; `soft` a check or a record beside it; `normal` everything else. Weight, not hue. */
  style?: FlowEdgeStyle;
  exit?: FlowSide;
  enter?: FlowSide;
}

export interface FlowLane {
  id: string;
  label: string;
  /** 'col' lanes are vertical bands with their label on top; 'row' lanes are horizontal with their label on the left. */
  axis: 'col' | 'row';
  start: number;
  span: number;
}

/** One diagram in one language, ready to draw. */
export interface FlowSheet {
  id: string;
  title: string;
  /** The one question this diagram answers. */
  question: string;
  cols: number;
  rows: number;
  /** The width of a cell in this diagram (default CELL_W): wider for long words, narrower for many columns. */
  cellW?: number;
  lanes: FlowLane[];
  nodes: FlowNode[];
  edges: FlowEdge[];
  /** Every node id once, in the order the step-through walks them and the accessible list prints them. */
  steps: string[];
}

// ── geometry ─────────────────────────────────────────────────────────────────────────────────────────────────────────

export const CELL_W = 176;
export const CELL_H = 76;
const GAP_X = 18;
const GAP_Y = 18;
/** Above the boxes when the sheet has column lanes (their labels). */
const COL_LANE_HEAD = 34;
/** Left of the boxes when the sheet has row lanes (their labels, written across). */
const ROW_LANE_HEAD = 112;
const MARGIN = 6;
const PAD_X = 8;
const PAD_Y = 8;
export const LABEL_SIZE = 15;
export const SUB_SIZE = 14;
export const EDGE_LABEL_SIZE = 14;
export const LANE_LABEL_SIZE = 14;
export const BADGE_SIZE = 14;

export interface FlowBox { x: number; y: number; w: number; h: number }

export type FlowPrim =
  | { t: 'rect'; x: number; y: number; w: number; h: number; rx: number; cls: string }
  | { t: 'path'; d: string; cls: string; marker?: FlowEdgeStyle }
  | { t: 'circle'; cx: number; cy: number; r: number; cls: string }
  | { t: 'text'; x: number; y: number; lines: string[]; size: number; lineHeight: number; anchor: 'start' | 'middle'; cls: string };

export interface FlowGroup { key: string; nodeId?: string; prims: FlowPrim[] }

export interface FlowLayout {
  width: number;
  height: number;
  /** Lanes and edges first, then one group per node in the walk's order (so Tab follows the walk), then the edge labels. */
  groups: FlowGroup[];
  boxes: Record<string, FlowBox>;
}

/** A generous estimate of a sans-serif's advance width at `size`, so text never overruns. */
export function textWidth(text: string, size: number, bold = false): number {
  let units = 0;
  for (const ch of text) {
    if (ch === ' ') units += 0.28;
    else if ("iIl.,:;|!'’".includes(ch)) units += 0.3;
    else if ('mwMW'.includes(ch)) units += 0.86;
    else if (ch >= 'A' && ch <= 'Z') units += 0.66;
    else units += 0.56;
  }
  return units * size * (bold ? 1.06 : 1);
}

/** Greedy word wrap to a width. A word longer than the line is kept whole (overflowingNodes reports it). */
export function wrap(text: string, width: number, size: number, bold = false): string[] {
  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (line && textWidth(candidate, size, bold) > width) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  }
  if (line) lines.push(line);
  return lines;
}

type Grid = Pick<FlowSheet, 'cols' | 'rows' | 'lanes' | 'cellW'>;

function cw(sheet: Grid): number {
  return sheet.cellW ?? CELL_W;
}

export function canvasSize(sheet: Grid): { width: number; height: number; top: number; left: number } {
  const hasCol = sheet.lanes.some((l) => l.axis === 'col');
  const hasRow = sheet.lanes.some((l) => l.axis === 'row');
  const top = MARGIN + (hasCol ? COL_LANE_HEAD : 8);
  const left = MARGIN + (hasRow ? ROW_LANE_HEAD : 8);
  return {
    top,
    left,
    width: left + sheet.cols * cw(sheet) + (sheet.cols - 1) * GAP_X + 8 + MARGIN,
    height: top + sheet.rows * CELL_H + (sheet.rows - 1) * GAP_Y + 8 + MARGIN,
  };
}

export function boxOf(sheet: Grid, node: FlowNode): FlowBox {
  const { top, left } = canvasSize(sheet);
  const w = node.w ?? 1;
  const h = node.h ?? 1;
  return {
    x: left + node.col * (cw(sheet) + GAP_X),
    y: top + node.row * (CELL_H + GAP_Y),
    w: w * cw(sheet) + (w - 1) * GAP_X,
    h: h * CELL_H + (h - 1) * GAP_Y,
  };
}

function innerWidth(node: FlowNode, box: FlowBox): number {
  return box.w - PAD_X * 2 - (node.kind === 'rule' ? 8 : 0) - (node.kind === 'decision' ? 28 : 0) - (node.kind === 'person' ? 12 : 0);
}

function roomOf(node: FlowNode, box: FlowBox): number {
  return box.h - PAD_Y * 2 - (node.kind === 'data' ? 14 : 0) - (node.kind === 'output' ? 8 : 0);
}

interface NodeText { label: string[]; sub: string[]; needed: number; inner: number }

function nodeText(node: FlowNode, box: FlowBox): NodeText {
  const inner = innerWidth(node, box);
  const label = wrap(node.label, inner, LABEL_SIZE, true);
  const sub = node.sub ? wrap(node.sub, inner, SUB_SIZE) : [];
  const needed = label.length * LABEL_SIZE * 1.22 + (sub.length ? 4 + sub.length * SUB_SIZE * 1.25 : 0);
  return { label, sub, needed, inner };
}

/** Nodes whose words do not fit their box (too many lines, or a word wider than the box). A test asserts none. */
export function overflowingNodes(sheet: FlowSheet): string[] {
  return sheet.nodes.filter((node) => {
    const box = boxOf(sheet, node);
    const t = nodeText(node, box);
    const tooWide = t.label.some((l) => textWidth(l, LABEL_SIZE, true) > t.inner) || t.sub.some((l) => textWidth(l, SUB_SIZE) > t.inner);
    return tooWide || t.needed > roomOf(node, box);
  }).map((n) => n.id);
}

/** Lanes whose label does not fit (a column lane's on one line; a row lane's in its left column). A test asserts none. */
export function overflowingLanes(sheet: FlowSheet): string[] {
  return sheet.lanes.filter((lane) => {
    if (lane.axis === 'col') return textWidth(lane.label, LANE_LABEL_SIZE, true) > lane.span * cw(sheet) + (lane.span - 1) * GAP_X - 12;
    const lines = wrap(lane.label, ROW_LANE_HEAD - 20, LANE_LABEL_SIZE, true);
    return lines.some((l) => textWidth(l, LANE_LABEL_SIZE, true) > ROW_LANE_HEAD - 20)
      || lines.length * LANE_LABEL_SIZE * 1.25 > lane.span * CELL_H + (lane.span - 1) * GAP_Y;
  }).map((l) => l.id);
}

/** Nodes that share a cell, or stand outside the grid. A test asserts none. */
export function misplacedNodes(sheet: FlowSheet): string[] {
  const taken = new Map<string, string>();
  const bad = new Set<string>();
  for (const n of sheet.nodes) {
    const w = n.w ?? 1;
    const h = n.h ?? 1;
    if (n.col < 0 || n.row < 0 || n.col + w > sheet.cols || n.row + h > sheet.rows) bad.add(n.id);
    for (let c = n.col; c < n.col + w; c++) {
      for (let r = n.row; r < n.row + h; r++) {
        const k = `${c},${r}`;
        const other = taken.get(k);
        if (other) { bad.add(n.id); bad.add(other); } else taken.set(k, n.id);
      }
    }
  }
  return [...bad];
}

function shapePrims(node: FlowNode, b: FlowBox): FlowPrim[] {
  const { x, y, w, h } = b;
  switch (node.kind) {
    case 'person':
      return [{ t: 'rect', x, y, w, h, rx: Math.min(h / 2, 30), cls: 'fd-shape fd-person' }];
    case 'ai':
      return [{ t: 'rect', x, y, w, h, rx: 8, cls: 'fd-shape fd-ai' }];
    case 'data': {
      const ry = 8;
      return [
        { t: 'path', d: `M${x} ${y + ry}A${w / 2} ${ry} 0 0 1 ${x + w} ${y + ry}V${y + h - ry}A${w / 2} ${ry} 0 0 1 ${x} ${y + h - ry}Z`, cls: 'fd-shape fd-data' },
        { t: 'path', d: `M${x} ${y + ry}A${w / 2} ${ry} 0 0 0 ${x + w} ${y + ry}`, cls: 'fd-inner' },
      ];
    }
    case 'output': {
      const a = 7;
      const q = w / 4;
      return [{
        t: 'path',
        d: `M${x} ${y}H${x + w}V${y + h - a}C${x + w - q} ${y + h - 3 * a} ${x + w - 2 * q} ${y + h + a} ${x + w / 2} ${y + h - a}C${x + q * 1.5} ${y + h - 3 * a} ${x + q} ${y + h + a} ${x} ${y + h - a}Z`,
        cls: 'fd-shape fd-output',
      }];
    }
    case 'decision': {
      const c = 18;
      return [{ t: 'path', d: `M${x + c} ${y}H${x + w - c}L${x + w} ${y + h / 2}L${x + w - c} ${y + h}H${x + c}L${x} ${y + h / 2}Z`, cls: 'fd-shape fd-decision' }];
    }
    case 'rule':
      return [
        { t: 'rect', x, y, w, h, rx: 4, cls: 'fd-shape fd-rule' },
        { t: 'rect', x, y, w: 7, h, rx: 2, cls: 'fd-rule-bar' },
      ];
    case 'external':
      return [
        { t: 'rect', x, y, w, h, rx: 4, cls: 'fd-shape fd-external' },
        { t: 'rect', x: x + 4, y: y + 4, w: w - 8, h: h - 8, rx: 2, cls: 'fd-inner-box' },
      ];
    default:
      return [{ t: 'rect', x, y, w, h, rx: 10, cls: 'fd-shape fd-step' }];
  }
}

function nodePrims(node: FlowNode, b: FlowBox, badge: number | null): FlowPrim[] {
  const prims = shapePrims(node, b);
  const t = nodeText(node, b);
  const cx = b.x + b.w / 2 + (node.kind === 'rule' ? 4 : 0);
  const top = b.y + (node.kind === 'data' ? 14 : 0);
  const room = b.h - (node.kind === 'data' ? 14 : 0) - (node.kind === 'output' ? 8 : 0);
  let cursor = top + Math.max(PAD_Y, (room - t.needed) / 2);
  prims.push({ t: 'text', x: cx, y: cursor + LABEL_SIZE - 2, lines: t.label, size: LABEL_SIZE, lineHeight: LABEL_SIZE * 1.22, anchor: 'middle', cls: 'fd-label' });
  cursor += t.label.length * LABEL_SIZE * 1.22;
  if (t.sub.length) {
    prims.push({ t: 'text', x: cx, y: cursor + 4 + SUB_SIZE - 2, lines: t.sub, size: SUB_SIZE, lineHeight: SUB_SIZE * 1.25, anchor: 'middle', cls: 'fd-sub' });
  }
  if (badge !== null) {
    prims.push({ t: 'circle', cx: b.x + 2, cy: b.y + 2, r: 13, cls: 'fd-badge' });
    prims.push({ t: 'text', x: b.x + 2, y: b.y + 7, lines: [String(badge)], size: BADGE_SIZE, lineHeight: BADGE_SIZE, anchor: 'middle', cls: 'fd-badge-text' });
  }
  return prims;
}

function anchor(b: FlowBox, side: FlowSide): [number, number] {
  switch (side) {
    case 'left': return [b.x, b.y + b.h / 2];
    case 'right': return [b.x + b.w, b.y + b.h / 2];
    case 'top': return [b.x + b.w / 2, b.y];
    default: return [b.x + b.w / 2, b.y + b.h];
  }
}

function sidesFor(a: FlowBox, b: FlowBox): [FlowSide, FlowSide] {
  if (a.x + a.w <= b.x) return ['right', 'left'];
  if (b.x + b.w <= a.x) return ['left', 'right'];
  if (a.y + a.h <= b.y) return ['bottom', 'top'];
  return ['top', 'bottom'];
}

/** An orthogonal route between two anchors, with the point its label sits at. */
export function route(a: FlowBox, b: FlowBox, exit?: FlowSide, enter?: FlowSide): { d: string; label: [number, number] } {
  const auto = sidesFor(a, b);
  const s1 = exit ?? auto[0];
  const s2 = enter ?? auto[1];
  const [x1, y1] = anchor(a, s1);
  const [x2, y2] = anchor(b, s2);
  const h1 = s1 === 'left' || s1 === 'right';
  const h2 = s2 === 'left' || s2 === 'right';
  if (h1 && h2) {
    const mx = (x1 + x2) / 2;
    if (Math.abs(y1 - y2) < 1) return { d: `M${x1} ${y1}H${x2}`, label: [mx, y1] };
    return { d: `M${x1} ${y1}H${mx}V${y2}H${x2}`, label: [mx, (y1 + y2) / 2] };
  }
  if (!h1 && !h2) {
    const my = (y1 + y2) / 2;
    if (Math.abs(x1 - x2) < 1) return { d: `M${x1} ${y1}V${y2}`, label: [x1, my] };
    return { d: `M${x1} ${y1}V${my}H${x2}V${y2}`, label: [(x1 + x2) / 2, my] };
  }
  if (h1) return { d: `M${x1} ${y1}H${x2}V${y2}`, label: [(x1 + x2) / 2, y1] };
  return { d: `M${x1} ${y1}V${y2}H${x2}`, label: [x1, (y1 + y2) / 2] };
}

/** The walk's number of each node (1-based), from `steps`. */
export function stepNumbers(sheet: FlowSheet): Map<string, number> {
  return new Map(sheet.steps.map((id, i) => [id, i + 1]));
}

export function layout(sheet: FlowSheet): FlowLayout {
  const size = canvasSize(sheet);
  const groups: FlowGroup[] = [];
  const boxes: Record<string, FlowBox> = {};
  for (const node of sheet.nodes) boxes[node.id] = boxOf(sheet, node);
  const numbers = stepNumbers(sheet);

  for (const lane of sheet.lanes) {
    const prims: FlowPrim[] = [];
    if (lane.axis === 'col') {
      const x = size.left + lane.start * (cw(sheet) + GAP_X) - GAP_X / 2 + 3;
      const w = lane.span * cw(sheet) + lane.span * GAP_X - 6;
      prims.push({ t: 'rect', x, y: MARGIN, w, h: size.height - 2 * MARGIN, rx: 8, cls: 'fd-lane' });
      prims.push({ t: 'text', x: x + w / 2, y: MARGIN + 22, lines: [lane.label], size: LANE_LABEL_SIZE, lineHeight: LANE_LABEL_SIZE, anchor: 'middle', cls: 'fd-lane-label' });
    } else {
      const y = size.top + lane.start * (CELL_H + GAP_Y) - GAP_Y / 2 + 3;
      const h = lane.span * CELL_H + lane.span * GAP_Y - 6;
      prims.push({ t: 'rect', x: MARGIN, y, w: size.width - 2 * MARGIN, h, rx: 8, cls: 'fd-lane' });
      const lines = wrap(lane.label, ROW_LANE_HEAD - 20, LANE_LABEL_SIZE, true);
      const lh = LANE_LABEL_SIZE * 1.25;
      prims.push({ t: 'text', x: MARGIN + 12, y: y + h / 2 - ((lines.length - 1) * lh) / 2 + 5, lines, size: LANE_LABEL_SIZE, lineHeight: lh, anchor: 'start', cls: 'fd-lane-label' });
    }
    groups.push({ key: `lane-${lane.id}`, prims });
  }

  const labels: FlowPrim[] = [];
  sheet.edges.forEach((edge, index) => {
    const a = boxes[edge.from];
    const b = boxes[edge.to];
    if (!a || !b) return;
    const style = edge.style ?? 'normal';
    const path = route(a, b, edge.exit, edge.enter);
    groups.push({ key: `edge-${index}`, prims: [{ t: 'path', d: path.d, cls: `fd-edge fd-edge-${style}`, marker: style }] });
    if (edge.label) {
      const lines = wrap(edge.label, 150, EDGE_LABEL_SIZE);
      const width = Math.max(...lines.map((l) => textWidth(l, EDGE_LABEL_SIZE))) + 12;
      const height = lines.length * 17 + 6;
      labels.push({ t: 'rect', x: path.label[0] - width / 2, y: path.label[1] - height / 2, w: width, h: height, rx: 4, cls: 'fd-edge-label-bg' });
      labels.push({ t: 'text', x: path.label[0], y: path.label[1] - height / 2 + 16, lines, size: EDGE_LABEL_SIZE, lineHeight: 17, anchor: 'middle', cls: 'fd-edge-label' });
    }
  });

  const byId = new Map(sheet.nodes.map((n) => [n.id, n]));
  const order = [...sheet.steps.filter((id) => byId.has(id)), ...sheet.nodes.map((n) => n.id).filter((id) => !numbers.has(id))];
  for (const id of order) {
    const node = byId.get(id) as FlowNode;
    groups.push({ key: `node-${id}`, nodeId: id, prims: nodePrims(node, boxes[id] as FlowBox, numbers.get(id) ?? null) });
  }
  groups.push({ key: 'edge-labels', prims: labels });
  return { width: size.width, height: size.height, groups, boxes };
}

/**
 * The drawing's styles. A host sets the --fd-* tokens or takes these defaults; `.fd-host[data-theme="dark"]` (or "auto" under
 * a dark preference) switches to the dark set. Selection and "you are here" use the accent as an outline, never a fill.
 */
export const FLOW_CSS = `
.fd-host { --fd-ink: #1e1b4b; --fd-ink-2: #334155; --fd-muted: #475569; --fd-card: #ffffff; --fd-lane: #f1f5f9;
  --fd-tint: #eef2ff; --fd-rule: #cbd5e1; --fd-accent: #4f46e5; --fd-on-accent: #ffffff; --fd-link: #4338ca; color: var(--fd-ink); }
.fd-host[data-theme="dark"] { --fd-ink: #e2e8f0; --fd-ink-2: #cbd5e1; --fd-muted: #a3b1c6; --fd-card: #0f172a; --fd-lane: #1e293b;
  --fd-tint: #1e1b4b; --fd-rule: #475569; --fd-accent: #a5b4fc; --fd-on-accent: #0f172a; --fd-link: #c7d2fe; }
@media (prefers-color-scheme: dark) {
  .fd-host[data-theme="auto"] { --fd-ink: #e2e8f0; --fd-ink-2: #cbd5e1; --fd-muted: #a3b1c6; --fd-card: #0f172a; --fd-lane: #1e293b;
    --fd-tint: #1e1b4b; --fd-rule: #475569; --fd-accent: #a5b4fc; --fd-on-accent: #0f172a; --fd-link: #c7d2fe; }
}
.fd-panel { background: var(--fd-card); border: 1px solid var(--fd-rule); border-radius: 8px; }
.fd-muted { color: var(--fd-muted); }
.fd-link { color: var(--fd-link); }
.fd-accent-text { color: var(--fd-link); }
.fd-btn { background: var(--fd-card); color: var(--fd-ink); border: 1px solid var(--fd-rule); }
.fd-btn:hover:not(:disabled) { border-color: var(--fd-accent); }
.fd-btn-primary { background: var(--fd-accent); color: var(--fd-on-accent); border-color: var(--fd-accent); }
.fd-btn:focus-visible, .fd-link:focus-visible, .fd-host summary:focus-visible { outline: 2px solid var(--fd-accent); outline-offset: 2px; }
.fd-svg { display: block; font-family: inherit; }
.fd-svg text { fill: var(--fd-ink); }
.fd-lane { fill: var(--fd-lane); stroke: none; }
.fd-lane-label { fill: var(--fd-muted) !important; font-weight: 700; }
.fd-shape { fill: var(--fd-card); stroke: var(--fd-ink); stroke-width: 1.4; }
.fd-person { stroke-width: 2; }
.fd-ai { stroke-dasharray: 6 4; stroke-width: 1.8; }
.fd-data { fill: var(--fd-tint); }
.fd-decision, .fd-output { stroke-width: 1.8; }
.fd-step { stroke-width: 1.6; }
.fd-rule-bar { fill: var(--fd-ink); stroke: none; }
.fd-inner { fill: none; stroke: var(--fd-ink); stroke-width: 1; }
.fd-inner-box { fill: none; stroke: var(--fd-ink); stroke-width: 0.8; }
.fd-label { font-weight: 700; }
.fd-sub { fill: var(--fd-ink-2) !important; }
.fd-badge { fill: var(--fd-ink); }
.fd-badge-text { fill: var(--fd-card) !important; font-weight: 700; }
.fd-edge { fill: none; stroke-linejoin: round; }
.fd-edge-normal { stroke: var(--fd-ink-2); stroke-width: 1.6; }
.fd-edge-strong { stroke: var(--fd-ink); stroke-width: 3; }
.fd-edge-soft { stroke: var(--fd-muted); stroke-width: 1.2; stroke-dasharray: 4 3; }
.fd-arrow-normal { fill: var(--fd-ink-2); }
.fd-arrow-strong { fill: var(--fd-ink); }
.fd-arrow-soft { fill: var(--fd-muted); }
.fd-edge-label-bg { fill: var(--fd-card); stroke: var(--fd-rule); stroke-width: 0.8; }
.fd-edge-label { fill: var(--fd-ink-2) !important; }
.fd-node { cursor: pointer; outline: none; }
.fd-node:hover .fd-shape { stroke: var(--fd-accent); }
.fd-node:focus-visible .fd-shape { stroke: var(--fd-accent); stroke-width: 3; }
.fd-node.fd-selected .fd-shape { stroke: var(--fd-accent); stroke-width: 3.6; }
.fd-node.fd-current .fd-shape { stroke: var(--fd-accent); stroke-width: 3; stroke-dasharray: none; }
.fd-node.fd-selected .fd-badge, .fd-node.fd-current .fd-badge { fill: var(--fd-accent); }
`;
